import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import hubLayout from 'styles/css/hubLayout.module.css';
import { Contract, ContractProductCount } from 'type/type';
import DaumPostcode from "react-daum-postcode";
import Modal from "react-modal";
import { calculDayDateToString, calculYearDateToString, contractStatusLabel } from 'pages/utils/commonUtils';
import { decryptAES256, getContractByIdx, maxLengthCheck, putContract } from 'api/api';
import Loading from 'components/common/Loading';
import { useModal } from 'components/common/ModalLayout';

// 다음주소 팝업창 theme
const themeObj = {
  bgColor: "#FFFFFF", //바탕 배경색
  searchBgColor: "#FFFFFF", //검색창 배경색
  contentBgColor: "#FFFFFF", //본문 배경색(검색결과,결과없음,첫화면,검색서제스트)
  pageBgColor: "#162525", //페이지 배경색
  textColor: "#162525", //기본 글자색
  queryTextColor: "#162525", //검색창 글자색
  outlineColor: "#444444" //테두리
};

const AddrInputComponent = ({ name, title, data, handleChange, register, errors, setValue, readOnly }: { name: string, title: string, data: Contract | any, handleChange: Function, register: Function, errors: any, setValue: Function, readOnly?: boolean }) => {
  const postCodeName = name.includes('company') ? 'company_post_code' : 'post_code';
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const openModal = () => {
    setModalIsOpen(true);
    window.history.pushState({ modalIsOpen: true }, '');
  }
  const closeModal = () => {
    setModalIsOpen(false);
    if (window.history.state?.modalIsOpen) {
      window.history.back(); // 히스토리 상태 복구
    }
  }

  function openDaumPostcode(data: any) {
    const zoneCode:string = data.zonecode; // 우편번호
    const roadAddress:string = data.roadAddress; // 도로명주소
    handleChange({ target : { name : name, value : roadAddress } });
    handleChange({ target : { name : name.includes('company') ? 'company_post_code' : 'post_code', value : zoneCode } });
    // react-hook-form의 setValue 사용
    setValue(postCodeName, zoneCode, { shouldValidate: true });
    setValue(name, roadAddress, { shouldValidate: true });
    closeModal();
  }

  // 뒤로가기 이벤트 처리
  useEffect(() => {
    const handlePopState = () => {
      if (modalIsOpen) {
        closeModal();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [modalIsOpen]);
  return (
    <>
      <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
        <label className={`${hubLayout.imp}`}>{title}</label>
        <input type="text" className={`${hubLayout.width_100} ${hubLayout.margin_right_10} ${errors?.[postCodeName] && hubLayout.border_red}`} readOnly
          placeholder="우편번호" value={data?.[postCodeName] || ""}
          {...register(`${postCodeName}`, {
            required: {
              value: true,
              message: '주소를 검색하세요.',
            },
          })}
        />
        <button type="button" className={`${hubLayout.btn} ${hubLayout.width_100} ${hubLayout.medium} ${hubLayout.blue}`} onClick={openModal} disabled={readOnly}>검색</button>
        <input type="text" placeholder="주소를 검색하세요." value={data?.[name] || ""} className={`${errors?.[name] && hubLayout.border_red}`} readOnly
          {...register(`${name}`, {
            required: {
              value: true,
              message: '주소를 검색하세요.',
            },
          })}
        />
        <input name={`${name}_detail`} type="text" placeholder="상세주소 입력" value={data?.[`${name}_detail`] || ""} className={`${errors?.[`${name}_detail`] && hubLayout.border_red}`} readOnly={readOnly} 
          {...register(`${name}_detail`, {
            required: {
              value: true,
              message: '상세 주소를 입력하세요.',
            },
            onChange: (e:any) => handleChange(e)
          })}
        />
        {(errors?.[postCodeName] || errors?.[name] || errors?.[`${name}_detail`]) && 
          <p className={`${hubLayout.tx_red}`}>
            {errors?.[postCodeName]?.message || errors?.[name]?.message || errors?.[`${name}_detail`]?.message}
          </p>
        }
      </li>
      {modalIsOpen &&
        <Modal
          isOpen={modalIsOpen} // 모달 열림/닫힘 상태
          onRequestClose={closeModal} // 모달 외부 클릭 시 닫힘
          ariaHideApp={false}
          style={{
            content: {
              width: '100%',
              top: "40%",
              left: "50%",
              right: "auto",
              bottom: "auto",
              transform: "translate(-50%, -40%)",
            },
            overlay: {
              backgroundColor: "rgba(0,0,0,.7)",
              zIndex: "10000",
            },
          }}
        >
          <DaumPostcode
            onComplete={openDaumPostcode}
            style={themeObj}
          />
        </Modal>
      }
    </>
  );
}

const InputComponent = ({ name, title, data, handleChange, register, errors, readOnly, placeholder }: { name: string, title: string, data: Contract | any, handleChange: Function, register: Function, errors: any, readOnly?: boolean, placeholder?: string }) => {
  const [birthdayFirst, setBirthdayFirst] = useState<string>(data?.birthday.slice(0, 6) || "");
  const [birthdayLast, setBirthdayLast] = useState<string>(data?.birthday.slice(6) || "");
  const [businessNumber1, setBusinessNumber1] = useState<string>(data?.business_number.slice(0, 3) || "");
  const [businessNumber2, setBusinessNumber2] = useState<string>(data?.business_number.slice(3, 5) || "");
  const [businessNumber3, setBusinessNumber3] = useState<string>(data?.business_number.slice(5, 10) || "");
  const contractStatus = contractStatusLabel.find(item => item.code === data?.contract_status)?.name || "";

  function birthdayHandler(event: any) {
    const { name, value } = event.target;
    const numericValue = value.replace(/[^0-9]/g, "");
    let birthday_first:string = '';
    let birthday_last:string = '';
    if (name === 'birthday_first') {
      birthday_first = numericValue;
      birthday_last = birthdayLast;
      setBirthdayFirst(numericValue);
    } else if (name === 'birthday_last') {
      birthday_first = birthdayFirst;
      birthday_last = numericValue;
      setBirthdayLast(numericValue);
    }
    handleChange({ target : { name : 'birthday', value : birthday_first + birthday_last } });
  }
  function businessNumberHandler(event: any) {
    const { name, value } = event.target;
    const numericValue = value.replace(/[^0-9]/g, "");
    let business_number1:string = '';
    let business_number2:string = '';
    let business_number3:string = '';
    if (name === 'business_number1') {
      business_number1 = numericValue;
      business_number2 = businessNumber2;
      business_number3 = businessNumber3;
      setBusinessNumber1(numericValue);
    } else if (name === 'business_number2') {
      business_number1 = businessNumber1;
      business_number2 = numericValue;
      business_number3 = businessNumber3;
      setBusinessNumber2(numericValue);
    } else if (name === 'business_number3') {
      business_number1 = businessNumber1;
      business_number2 = businessNumber2;
      business_number3 = numericValue;
      setBusinessNumber3(numericValue);
    }
    handleChange({ target : { name : 'business_number', value : business_number1 + business_number2 + business_number3 } });
  }
  function handleToNumericValue(event: any) {
    const input = event.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    event.target.value = numericValue;
    handleChange(event);
  }
  return (
    <>
      {name === "birthday" ?
        <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
          <label className={`${hubLayout.imp}`}>{title}</label>
          <input type='text' placeholder={placeholder} value={birthdayFirst || ""} style={{ width: '40%' }} maxLength={6} className={`${errors.birthday_first && hubLayout.border_red}`} readOnly={readOnly}
            {...register(`${name}_first`, {
              required: {
                value: true,
                message: placeholder,
              },
              pattern : {
                value: /([0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[1,2][0-9]|3[0,1]))/g,
                message: `정확한 ${placeholder}`,
              },
              onChange: (e:any) => {
                maxLengthCheck(e);
                birthdayHandler(e);
              }
            })}
          />
          <span style={{ margin: '0 5px' }}>-</span>
          <input type='text' value={birthdayLast || ""} style={{ width: '15%' }} maxLength={1} className={`${errors.birthday_last && hubLayout.border_red}`} readOnly={readOnly}
            {...register(`${name}_last`, {
              required: {
                value: true,
                message: placeholder,
              },
              pattern : {
                value: /^[1-4]{1}$/g,
                message: `정확한 ${placeholder}`,
              },
              onChange: (e:any) => {
                maxLengthCheck(e);
                birthdayHandler(e);
              }
            })}
          />
          * * * * * *
          {(errors.birthday_first || errors.birthday_last) && 
            <p className={`${hubLayout.tx_red}`}>
              {errors.birthday_first?.message || errors.birthday_last?.message}
            </p>
          }
        </li>
      : name === "business_number" ?
        <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
          <label className={`${hubLayout.imp}`}>{title}</label>
          <input type='text' value={businessNumber1 || ""} style={{ width: '30%' }} maxLength={3} className={`${errors.business_number1 && hubLayout.border_red}`} readOnly={readOnly}
            {...register(`${name}1`, {
              required: {
                value: true,
                message: placeholder,
              },
              pattern : {
                value: /^[0-9]{3}$/g,
                message: `정확한 ${placeholder}`,
              },
              onChange: (e:any) => {
                maxLengthCheck(e);
                businessNumberHandler(e);
              }
            })}
          />
          <span style={{ margin: '0 5px' }}>-</span>
          <input type='text' value={businessNumber2 || ""} style={{ width: '20%' }} maxLength={2} className={`${errors.business_number2 && hubLayout.border_red}`} readOnly={readOnly}
            {...register(`${name}2`, {
              required: {
                value: true,
                message: placeholder,
              },
              pattern : {
                value: /^[0-9]{2}$/g,
                message: `정확한 ${placeholder}`,
              },
              onChange: (e:any) => {
                maxLengthCheck(e);
                businessNumberHandler(e);
              }
            })}
          />
          <span style={{ margin: '0 5px' }}>-</span>
          <input type='text' value={businessNumber3 || ""} style={{ width: '40%' }} maxLength={5} className={`${errors.business_number3 && hubLayout.border_red}`} readOnly={readOnly}
            {...register(`${name}3`, {
              required: {
                value: true,
                message: placeholder,
              },
              pattern : {
                value: /^[0-9]{5}$/g,
                message: `정확한 ${placeholder}`,
              },
              onChange: (e:any) => {
                maxLengthCheck(e);
                businessNumberHandler(e);
              }
            })}
          />
          {(errors.business_number1 || errors.business_number2 || errors.business_number3) && 
            <p className={`${hubLayout.tx_red}`}>
              {errors.business_number1?.message || errors.business_number2?.message || errors.business_number3?.message}
            </p>
          }
        </li>
      : name === "price" ?
        <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
          <label className={`${hubLayout.imp}`}>{title}</label>
          <input type='text' value={data?.[name].toLocaleString("ko-KR") || ""} readOnly={readOnly}
            {...register(`price`, {
              required: {
                value: true,
                message: placeholder,
              },
              onChange: (e:any) => handleToNumericValue(e)
            })}
          />
        </li>
      : name === "contract_status" ?
        <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
          <label className={`${hubLayout.imp}`}>{title}</label>
          <input type='text' value={contractStatus} onChange={(e) => handleChange(e)} readOnly={readOnly}
            {...register(`${name}`, {
              required: true,
              onChange: (e:any) => handleChange(e)
            })}
          />
        </li>
      : name === "contract_no" ?
        <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
          <label className={`${hubLayout.imp}`}>{title}</label>
          <input type='text' value={data?.[name] || ""} maxLength={9} placeholder={placeholder} readOnly={readOnly}
            {...register(`contract_no`, {
              required: {
                value: true,
                message: placeholder,
              },
              pattern : {
                value: /^[0-9]{9}$/g,
                message: '계약번호는 9자리로 입력해주세요.',
              },
              onChange: (e:any) => {
                maxLengthCheck(e);
                handleToNumericValue(e);
              }
            })}
          />
          {errors?.[name] && <p className={`${errors?.[name] && hubLayout.tx_red}`}>{errors?.[name]?.message}</p>}
        </li>
      : name.includes('mobile') ?
        <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
          <label className={`${hubLayout.imp}`}>{title}</label>
          <input type='text' value={data?.[name] || ""} maxLength={11} placeholder={placeholder} className={`${errors?.[name] && hubLayout.border_red}`} readOnly={readOnly}
            {...register(`${name}`, {
              required: {
                value: true,
                message: placeholder,
              },
              pattern : {
                value: /^01[016789]\d{3,4}\d{4}$/g,
                message: `정확한 ${placeholder}`,
              },
              onChange: (e:any) => {
                maxLengthCheck(e);
                handleToNumericValue(e);
              }
            })}
          />
          {errors?.[name] && <p className={`${errors?.[name] && hubLayout.tx_red}`}>{errors?.[name]?.message}</p>}
        </li>
      :
        <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
          <label className={`${hubLayout.imp}`}>{title}</label>
          <input type='text' placeholder={placeholder} value={data?.[name] || ""} className={`${errors?.[name] && hubLayout.border_red}`} readOnly={readOnly}
            {...register(`${name}`, {
              required: {
                value: true,
                message: placeholder,
              },
              onChange: (e:any) => {
                if (name === 'bank_number') {
                  handleToNumericValue(e);
                } else {
                  handleChange(e);
                }
              }
            })}
          />
          {errors?.[name] && <p className={`${errors?.[name] && hubLayout.tx_red}`}>{errors?.[name]?.message}</p>}
        </li>
      }
    </>
  );
}
const DateComponent = ({ name, title, data, handleChange, readOnly }: { name: string, title: string, data: Contract | any,  handleChange: Function, readOnly?: boolean }) => {
  return (
    <>
      <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
        <label className={`${hubLayout.imp}`}>{title}</label>
        <input name={name} type="date" value={data?.[name] || ""} onChange={(e) => handleChange(e)} readOnly={readOnly} />
      </li>
    </>
  );
}

const SelectComponent = ({ name, title, data, handleChange, readOnly }: { name: string, title: string, data: Contract | any,  handleChange: Function, readOnly?: boolean }) => {
  return (
    <>
      <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
        <label className={`${hubLayout.imp}`}>{title}</label>
        <div className={`${hubLayout.inp_select}`}>
          {name === "term" ?
            <select className={`${hubLayout.inp_select}`} name={name} value={data?.[name] || "1"} onChange={(e) => handleChange(e)} disabled={readOnly} >
              {Array.from({ length: 5 }, (_, idx) => (
                <option key={`${name}_${idx}`} value={idx + 1}>{idx + 1}년</option>
              ))}
            </select>
          : name === "contract_product" ?
            <select className={`${hubLayout.inp_select}`} name={name} value={data?.[name] || ""} onChange={(e) => handleChange(e)} disabled={readOnly} >
              <option value={`기본형`}>기본형</option>
              <option value={`임대형`}>임대형</option>
            </select>
          : name.includes('_service') ?
            <select className={`${hubLayout.inp_select}`} name={name} value={data?.service_code?.[name] || 0} onChange={(e) => handleChange(e)} disabled={readOnly} >
              <option value={0}>자가방범</option>
              <option value={1}>무인관제</option>
              <option value={2}>무인관제</option>
            </select>
          :
            <select className={`${hubLayout.inp_select}`} name={name} value={data?.[name] || "0"} onChange={(e) => handleChange(e)} disabled={readOnly} >
              {Array.from({ length: 11 }, (_, idx) => (
                <option key={`${name}_${idx}`} value={idx}>{idx}대</option>
              ))}
            </select>
          }
        </div>
      </li>
    </>
  );
}

function ContractRegister() {
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { contractIdx } = useParams();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  
  const [step, setStep] = useState<number>(0);

  // useForm
  const { register: register1, formState: { errors: errors1, isValid: isValid1 }, setValue } = useForm<any>({
    mode: 'all',
  });
  // useForm
  const { register: register2, formState: { errors: errors2, isValid: isValid2 } } = useForm<any>({
    mode: 'all',
  });
  //2024-11-21
  const [contract, setContract] = useState<Contract>(Object);
  const [contractProductCount, setContractProductCount] = useState<ContractProductCount>(Object);

  const productCountHandleChange = (event: any) => {
    const { name, value } = event.target;
    setContractProductCount((prevData) => ({
      ...prevData,
      [name]: Number(value),
    }));
  }

  // 계약 onChange 이벤트
  const handleChange = (event: any) => {
    const { name, value } = event.target;
    if (["contract_date", "start_date"].includes(name) && value === '') {
      return;
    }
    if (name === "price") {
      setContract((prevData) => ({
        ...prevData,
        [name]: Number(value.replaceAll(',','')),
      }));
    }
    else if (name === "term") {
      let endDate = calculYearDateToString(new Date(contract.start_date), Number(value));
      endDate = calculDayDateToString(new Date(endDate), -1);
      setContract((prevData) => ({
        ...prevData,
        [name]: value,
        'end_date': endDate,
      }));
    }
    else if (name.includes('_service') > 0) {
      setContract((prevData) => ({
        ...prevData,
        'service_code': {
          [name] : Number(value)
        },
      }));
    }
    else {
      if (name === "contract_no") {
        setContractProductCount((prevData) => ({
          ...prevData,
          [name]: value,
        }));
      }
      if (name === "start_date") {
        let endDate = calculYearDateToString(new Date(value), Number(contract.term));
        endDate = calculDayDateToString(new Date(endDate), -1);
        setContract((prevData) => ({
          ...prevData,
          [name]: value,
          'end_date': endDate,
        }));
      }
      setContract((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  }

  const _editContract = async () => {
    const response = await putContract(contract);
    if (response.code === 200) {
      showModal("alert", "계약정보가 수정되었습니다. 본사 검토 및 승인 후 고객 계정과 장비를 등록할 수 있습니다.", {confirm: true, cancel: false},
        () => navigate("/agent/contract/management"),
        () => navigate("/agent/contract/management")
      );
    } else {
      showModal("alert", response.message);
    }
  }

  const getContracts = async () => {
    setLoading(false);
    const response = await getContractByIdx(Number(contractIdx));
    if (response.code === 200) {
      setIsEdit(response.data.contract_status !== 'A');
      
      // 복호화
      const REACT_APP_SECRET_KEY = process.env.REACT_APP_SECRET_KEY!;
      const REACT_APP_SECRET_IV = process.env.REACT_APP_SECRET_IV!;
      response.data.mobile = decryptAES256(response.data.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
      response.data.company_mobile = decryptAES256(response.data.company_mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
      response.data.bank_name = decryptAES256(response.data.bank_name, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
      response.data.bank_number = decryptAES256(response.data.bank_number, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);

      setContract(response.data);
      setContractProductCount(response.data.contract_product_count);
    } else {
      showModal("alert", response.message);
    }
    setLoading(true);
  };

  useEffect(() => {
    getContracts();
  },[]);
  return (
    <>
      {!loading ? <Loading /> :
        <div id="wrap">
          <div className={`${hubLayout.cnt_box}`}>
            <div className={`${hubLayout.acc__cnt}`}>
              <div className={`${hubLayout.sec_title} ${hubLayout.fixed_title}`}>
                <h2>고객 계약 수정</h2>
                <span onClick={() => navigate(-1)}>X</span>
              </div>
              {step === 0 ? 
                <>
                  <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`} style={{ marginTop: "4rem" }}>기본 정보</h2>
                  <ul className={`${hubLayout.inp_list}`}>
                    <InputComponent name={'contract_status'} title={'계약상태'} data={contract} handleChange={handleChange} register={register1} errors={errors1} readOnly={true} />
                    <InputComponent name={'contract_no'} title={'계약번호'} data={contract} handleChange={handleChange} register={register1} errors={errors1} readOnly={true} placeholder='계약번호 입력하세요.' />
                    <InputComponent name={'name'} title={'이름'} data={contract} handleChange={handleChange} register={register1} errors={errors1} readOnly={isEdit} placeholder='이름을 입력하세요.' />
                    <InputComponent name={'mobile'} title={'연락처'} data={contract} handleChange={handleChange} register={register1} errors={errors1} readOnly={isEdit} placeholder='연락처를 입력하세요.' />
                    <InputComponent name={'birthday'} title={'생년월일'} data={contract} handleChange={handleChange} register={register1} errors={errors1} readOnly={isEdit} placeholder='생년월일 6자리.' />
                    <AddrInputComponent name={'address'} title={'주소'} data={contract} handleChange={handleChange} register={register1} errors={errors1} setValue={setValue} readOnly={isEdit} />
                  </ul>
                  <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>사업장 정보</h2>
                  <ul className={`${hubLayout.inp_list}`}>
                    <InputComponent name={'company_name'} title={'상호'} data={contract} handleChange={handleChange} register={register1} errors={errors1} readOnly={isEdit} placeholder='상호를 입력하세요' />
                    <InputComponent name={'company_mobile'} title={'연락처'} data={contract} handleChange={handleChange} register={register1} errors={errors1} readOnly={isEdit} placeholder='연락처를 입력하세요' />
                    <InputComponent name={'business_number'} title={'사업자 번호'} data={contract} handleChange={handleChange} register={register1} errors={errors1} readOnly={isEdit} placeholder='사업자번호를 입력하세요.'/>
                    <AddrInputComponent name={'company_address'} title={'사업장 주소'} data={contract} handleChange={handleChange} register={register1} errors={errors1} setValue={setValue} readOnly={isEdit} />
                  </ul>
                  <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => navigate(-1)}>이전</button>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={() => setStep(step + 1)} disabled={!isValid1}>다음</button>
                  </div>
                </>
              : step === 1 ? 
                <>
                  <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`} style={{ marginTop: "4rem" }}>계약 정보</h2>
                  <ul className={`${hubLayout.inp_list}`}>
                    <DateComponent name={'contract_date'} title={'계약일자'} data={contract} handleChange={handleChange} readOnly={isEdit} />
                    <DateComponent name={'start_date'} title={'시작일자'} data={contract} handleChange={handleChange} readOnly={isEdit} />
                    <SelectComponent name={'term'} title={'계약기간'} data={contract} handleChange={handleChange} readOnly={isEdit} />
                    <SelectComponent name={'contract_product'} title={'계약상품'} data={contract} handleChange={handleChange} readOnly={isEdit} />
                    <SelectComponent name={'control_service'} title={'관제서비스'} data={contract} handleChange={handleChange} readOnly={isEdit} />
                    <InputComponent name={'price'} title={'계약금액(월/VAT포함)'} data={contract} handleChange={handleChange} register={register2} errors={errors2} readOnly={isEdit} placeholder='계약금액(월/VAT포함)을 입력하세요.'/>
                    <InputComponent name={'bank_name'} title={'은행'} data={contract} handleChange={handleChange} register={register2} errors={errors2} readOnly={isEdit} placeholder='은행을 입력하세요.' />
                    <InputComponent name={'bank_number'} title={'계좌번호'} data={contract} handleChange={handleChange} register={register2} errors={errors2} readOnly={isEdit} placeholder='계좌번호를 입력하세요.' />
                  </ul>
                  <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => setStep(step - 1)}>이전</button>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={() => setStep(step + 1)} disabled={!isValid2}>다음</button>
                  </div>
                </>
              : step === 2 &&
                <>
                  <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`} style={{ marginTop: "4rem" }}>장비 구성</h2>
                  <ul className={`${hubLayout.inp_list}`}>
                    <SelectComponent name={'nvr'} title={'NVR'} data={contractProductCount} handleChange={productCountHandleChange} readOnly={isEdit} />
                    <SelectComponent name={'dvr'} title={'DVR'} data={contractProductCount} handleChange={productCountHandleChange} readOnly={isEdit} />
                    <SelectComponent name={'dm_camera'} title={'실내카메라'} data={contractProductCount} handleChange={productCountHandleChange} readOnly={isEdit} />
                    <SelectComponent name={'bm_camera'} title={'실외카메라'} data={contractProductCount} handleChange={productCountHandleChange} readOnly={isEdit} />
                    {/* <SelectComponent name={'iot_box'} title={'IoT Box'} data={contractProductCount} handleChange={productCountHandleChange} readOnly={isEdit} />
                    <SelectComponent name={'access_auth_device'} title={'출입인증기'} data={contractProductCount} handleChange={productCountHandleChange} readOnly={isEdit} />
                    <SelectComponent name={'emergency_bell'} title={'비상벨'} data={contractProductCount} handleChange={productCountHandleChange} readOnly={isEdit} />
                    <SelectComponent name={'env_sensor'} title={'환경센서'} data={contractProductCount} handleChange={productCountHandleChange} readOnly={isEdit} /> */}
                  </ul>
                  <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={() => setStep(step - 1)}>이전</button>
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={_editContract} disabled={isEdit || !isValid2}>수정</button>
                  </div>
                </>
              }
            </div>
          </div>
        </div>
      }
    </>
  )
}

export default ContractRegister;